// MANTENIMIENTO DE LA AUTORIDAD COMPETENTE (25-sep-2026, pedido del operador).
//
// QUÉ RELLENA ESTO. El encabezado oficial del Formato Amarillo lleva la caja de la AC:
// su emblema, su nombre, su dirección, su teléfono, su correo y su web. Hasta ahora esos
// datos solo se cargaban por script contra la base; cada corrección exigía un despliegue
// de BD. Aquí se mantienen desde la pantalla, como cualquier otro parámetro.
//
// ⚠ POR QUÉ ES OTRA PESTAÑA Y NO UN CAMPO MÁS EN «AUTORIDADES Y PLAZOS».
// Aquella pantalla guarda con un PUT que SUSTITUYE la configuración entera —omitir un
// plazo lo deja sin configurar, y eso es deliberado: es lo que permite borrar un plazo
// mal cargado (RN-12)—. Si el contacto viajara por ahí, esta pantalla, que no muestra ni
// un plazo, tendría que reenviarlos todos para no borrarlos; el día que alguien olvidara
// uno, una autoridad se quedaría sin plazo regulatorio y nadie lo notaría hasta que
// venciera un caso. Con dos endpoints ese fallo no se puede cometer, y lo fija una
// prueba contra Postgres real (`tests/integracion/test_api_autoridades.py`).
//
// ⚠ EL NOMBRE DE LA AUTORIDAD SE VE PERO NO SE EDITA. Es la misma columna que ya
// mantiene «Autoridades y plazos»: dos pantallas escribiendo el mismo dato es cómo se
// consigue que dos personas se pisen sin enterarse. Se muestra porque sin él la fila no
// se reconoce, y se dice dónde se cambia.
//
// ⚠ ESTA PANTALLA NO DA DE ALTA MERCADOS. Solo edita las filas que existen. La clave de
// `parametro_pais` es una FK a `pais` y al registrar un caso el país va enlazado con su
// autoridad: crear una fila desde aquí tendría consecuencias en otro módulo. El servidor
// devuelve 404 si el país no la tiene.

import { useCallback, useEffect, useState } from 'react';
import { Building2, Globe, Image as IconoImagen, Mail, MapPin, Pencil, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ColumnaDeTabla } from '../components/ui';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Input, Modal, Mono, PageHeader,
  StatusBadge, SubidaDeArchivo,
} from '../components/ui';
import * as api from '../api/autoridades';
import { aFallo } from '../api/cliente';
import { revisarCorreo, telefonoValido } from '../lib/validacion';
import { filasPorAutoridad } from '../lib/autoridadesDelPais';
import type { Fallo } from '../api/cliente';

/** Lo que llega del servidor por fila. */
type RespuestaDelApi = Record<string, unknown>;
type FilaDeAutoridad = RespuestaDelApi;

/**
 * EL MISMO TOPE QUE EL SERVIDOR, Y COMPROBADO EN LOS DOS LADOS.
 *
 * Aquí para avisar antes de subir un archivo de 3 MB por una red lenta; allí porque una
 * carga por API no puede saltarse el límite. 200 KB de imagen ya decodificada: un emblema
 * oficial pesa muy por debajo, y el tope está para que nadie suba un escaneo que engorde
 * cada lectura del parámetro y cada impresión del formulario.
 */
const MAXIMO_DEL_LOGO = 200 * 1024;

const ACEPTA_IMAGEN = {
  accept: 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml',
  texto: 'PNG, JPG, GIF, WEBP o SVG · máx. 200 KB',
} as const;

/**
 * LA CASILLA DEL LOGO EN EL FORMATO AMARILLO, MEDIDA SOBRE EL DOCUMENTO RENDERIZADO.
 *
 * `impresion.py` da a la celda el 18 % de una hoja de 190 mm y limita la imagen con
 * `max-width:100%; max-height:58px`. Medido en el navegador: la celda ocupa 135 × 69 px
 * y su relleno (4px 6px) deja 123 × 58 px de dibujo, o sea unos 33 × 15 mm en el papel.
 *
 * ⚠ LA CASILLA ES APAISADA, ~2:1, Y ESO CAMBIA LA RECOMENDACIÓN. Un emblema CUADRADO no
 * se «adapta»: lo limita la altura, se reduce a 58 × 58 y deja más de la mitad del ancho
 * vacío, así que sale pequeño al lado del título. El operador preguntó por 400 × 400 el
 * 25-sep-2026 y esta constante existe para que la pantalla responda esa pregunta sola.
 */
const CAJA_DEL_LOGO = { ancho: 123, alto: 58 } as const;

/**
 * El tamaño que conviene subir: la casilla a resolución de impresión.
 *
 * 33 mm son 1,28 pulgadas y 15 mm son 0,60: a 300 ppp salen 384 × 181 px. Se redondea a
 * 390 × 180. Por debajo la imagen se estira al imprimir y sale borrosa; muy por encima
 * solo engorda el documento, porque el navegador la reduce igual.
 */
const RECOMENDADO = { ancho: 390, alto: 180 } as const;

/** Cómo quedará la imagen dentro de la casilla, con su proporción respetada. */
function comoEntraEnLaCasilla(ancho: number, alto: number) {
  const escala = Math.min(CAJA_DEL_LOGO.ancho / ancho, CAJA_DEL_LOGO.alto / alto, 1);
  return { ancho: Math.round(ancho * escala), alto: Math.round(alto * escala) };
}

/**
 * El aviso que corresponda, o `null` si la imagen entra bien.
 *
 * Son AVISOS y no errores: la imagen se guarda igual. Quien mantiene esto puede tener el
 * único emblema que existe de esa autoridad, y rechazárselo por las proporciones sería
 * dejar la casilla vacía, que es peor que dejarla pequeña.
 */
function avisoDelLogo(ancho: number, alto: number): string | null {
  const dibujo = comoEntraEnLaCasilla(ancho, alto);
  if (dibujo.ancho < CAJA_DEL_LOGO.ancho * 0.6) {
    return `Se dibujará a ${dibujo.ancho} × ${dibujo.alto} px y se verá pequeña junto al título. `
      + `Usa una versión horizontal (~${RECOMENDADO.ancho} × ${RECOMENDADO.alto} px).`;
  }
  if (ancho < RECOMENDADO.ancho * 0.6) {
    return `Son ${ancho} × ${alto} px: entra bien, pero al imprimir se estira y puede verse `
      + `borrosa. Lo ideal son unos ${RECOMENDADO.ancho} × ${RECOMENDADO.alto} px.`;
  }
  return null;
}

/**
 * El tamaño real de una imagen ya incrustada.
 *
 * Un SVG puede no declarar medidas en píxeles y devolver 0: entonces no se mide y no se
 * avisa de nada, que es lo correcto —es vectorial y escala sin perder nitidez—.
 */
const medirImagen = (incrustada: string): Promise<{ ancho: number; alto: number } | null> =>
  new Promise((resolver) => {
    const imagen = new Image();
    imagen.onload = () => resolver(
      imagen.naturalWidth && imagen.naturalHeight
        ? { ancho: imagen.naturalWidth, alto: imagen.naturalHeight }
        : null,
    );
    imagen.onerror = () => resolver(null);
    imagen.src = incrustada;
  });

/**
 * LOS TOPES DE CADA CAMPO, Y POR QUÉ ESTOS.
 *
 * Los fijó el operador (25-sep-2026) y el servidor los repite en su contrato: un tope
 * que solo viva en la pantalla no protege de una carga por API.
 *
 * ⚠ 100 EN «DIRECCIÓN» VA JUSTO. La más larga de las ya cargadas es la de SENASICA
 * —«Av. Insurgentes Sur 489, Col. Hipódromo, Alcaldía Cuauhtémoc, C.P. 06100, Ciudad de
 * México»— con 94 caracteres: entra, pero con 6 de margen. Si alguna autoridad publica
 * una dirección más larga, el tope hay que subirlo AQUÍ Y EN `esquemas.py`, o el
 * formulario no dejará guardar esa fila ni para corregirle el teléfono.
 */
const TOPES = { direccion: 100, telefono: 20, correo: 200, web: 100 } as const;

/**
 * «usados / tope», la forma que usa el resto de la aplicación.
 *
 * Es idéntica a la de `Destinatarios` a propósito: que dos pantallas cuenten distinto lo
 * mismo se lee como un fallo, aunque las dos cuenten bien.
 */
const cuentaAtras = (valor: string, tope: number): string => `${valor.length} / ${tope} caracteres`;

/**
 * Una web con forma de web. Deliberadamente laxa.
 *
 * Aquí se escribe `www.senasa.gob.pe` —así lo publican las autoridades y así sale
 * impreso—, no una URL completa, así que exigir `https://` rechazaría lo correcto. Lo
 * que se caza es el error de dedo que deja el encabezado con algo que no es una
 * dirección: un espacio en medio, o ningún punto.
 */
function problemaDeLaWeb(valor: string): string | null {
  if (!valor) return null;
  if (/\s/.test(valor)) return 'Una dirección web no lleva espacios.';
  if (!valor.includes('.')) return `«${valor}» no parece una dirección web: le falta el punto del dominio.`;
  return null;
}

const texto = (valor: unknown): string => (valor == null ? '' : String(valor));

/** Un guion largo, no una celda vacía: «no hay dato» y «no cargó» se leen distinto. */
const oGuion = (valor: unknown): ReactNode =>
  texto(valor) ? <span className="text-body">{texto(valor)}</span> : <span className="text-ink-soft">—</span>;

/**
 * El archivo, leído como imagen INCRUSTADA (`data:image/...;base64,...`).
 *
 * No se sube a un almacén ni se guarda una dirección: el Formato Amarillo se imprime
 * desde un HTML que arma el servidor, y un `<img>` que apunte a otro sitio puede llegar
 * vacío justo en el documento que importa. Incrustado viaja dentro del HTML.
 */
const comoImagenIncrustada = (archivo: File): Promise<string> =>
  new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result));
    lector.onerror = () => rechazar(new Error('No se pudo leer la imagen.'));
    lector.readAsDataURL(archivo);
  });

const CELDA_POR_COLUMNA: Record<string, (fila: FilaDeAutoridad) => ReactNode> = {
  autoridad: (fila) => (
    <div>
      <p className="font-semibold text-ink text-body">
        {texto(fila.nombre_pais) || texto(fila.pais)} · {texto(fila.autoridad)}
      </p>
      <Mono className="text-meta text-ink-soft">
        {texto(fila.pais)}{fila.codigo && fila.codigo !== fila.autoridad ? ` · ${texto(fila.codigo)}` : ''}
      </Mono>
    </div>
  ),
  direccion: (fila) => oGuion(fila.autoridad_direccion),
  telefono: (fila) => oGuion(fila.autoridad_telefono),
  correo: (fila) => oGuion(fila.autoridad_correo),
  web: (fila) => oGuion(fila.autoridad_web),
  // La lista NO trae la imagen —serían catorce emblemas en cada apertura para pintar,
  // como mucho, miniaturas—, así que dice si la hay. La imagen la trae el detalle.
  logo: (fila) => (fila.tiene_logo
    ? <StatusBadge tone="success" icon={IconoImagen}>Cargado</StatusBadge>
    : <StatusBadge tone="neutral" dot={false}>Sin logo</StatusBadge>),
};

const COLUMNAS = [
  { key: 'autoridad', label: 'País · autoridad' },
  { key: 'direccion', label: 'Dirección' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'correo', label: 'Email' },
  { key: 'web', label: 'Página web' },
  { key: 'logo', label: 'Logo' },
  { key: 'acciones', label: '' },
];

/** Lo que el formulario tiene entre manos mientras se edita. */
type EnEdicion = {
  pais: string;
  /** CC-02: la autoridad del país que se edita; `null` con un servidor anterior (va por el país). */
  codigo: string | null;
  nombrePais: string;
  autoridad: string;
  direccion: string;
  telefono: string;
  correo: string;
  web: string;
  /** La imagen incrustada, o cadena vacía si no hay. */
  logo: string;
};

/**
 * Los problemas de forma de cada campo. Vacío = todo bien.
 *
 * Se calcula entero en cada render y se muestra solo lo TOCADO: acusar de un correo
 * inválido a quien lleva escritas tres letras es regañar a alguien por no haber
 * terminado. Al pulsar «Guardar» se consideran tocados todos.
 */
function problemasDeForma(datos: EnEdicion): Partial<Record<keyof EnEdicion, string>> {
  const problemas: Partial<Record<keyof EnEdicion, string>> = {};
  const { error: errorDelCorreo } = revisarCorreo(datos.correo);
  if (datos.correo.trim() && errorDelCorreo) problemas.correo = errorDelCorreo;
  if (datos.telefono.trim() && !telefonoValido(datos.telefono)) {
    problemas.telefono = 'Un teléfono son entre 6 y 15 dígitos; se admiten «+», espacios, guiones y paréntesis.';
  }
  const problemaWeb = problemaDeLaWeb(datos.web.trim());
  if (problemaWeb) problemas.web = problemaWeb;
  return problemas;
}

function ModalDeContacto({ editando, error, guardando, onCambio, onCerrar, onGuardar }: {
  editando: EnEdicion | null;
  error: Fallo | null;
  guardando: boolean;
  onCambio: (cambio: Partial<EnEdicion>) => void;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const [problemaDelLogo, setProblemaDelLogo] = useState<string | null>(null);
  // El aviso de proporciones va SEPARADO del error: uno impide guardar y el otro no.
  const [avisoDeTamano, setAvisoDeTamano] = useState<string | null>(null);
  // Qué campos ha ABANDONADO ya el usuario. Un campo a medio escribir no se corrige.
  const [tocados, setTocados] = useState<Set<string>>(() => new Set());
  const tocar = (campo: keyof EnEdicion) => setTocados((p) => new Set(p).add(campo));

  const problemas = editando ? problemasDeForma(editando) : {};
  const visible = (campo: keyof EnEdicion): string | undefined =>
    (tocados.has(campo) ? problemas[campo] : undefined);
  const hayProblemas = Object.keys(problemas).length > 0;

  const intentarGuardar = () => {
    if (hayProblemas) {
      // Al intentar guardar se consideran tocados TODOS: si el error siguiera oculto, el
      // botón no haría nada y la pantalla parecería colgada.
      setTocados(new Set(Object.keys(problemas)));
      return;
    }
    onGuardar();
  };

  const elegirLogo = async (archivo: File | null) => {
    setProblemaDelLogo(null);
    setAvisoDeTamano(null);
    if (!archivo) return;
    if (archivo.size > MAXIMO_DEL_LOGO) {
      // Se avisa ANTES de leer el archivo: no tiene sentido cargar 3 MB en memoria para
      // luego decir que no caben.
      setProblemaDelLogo(
        `La imagen pesa ${Math.round(archivo.size / 1024)} KB y el máximo son ${MAXIMO_DEL_LOGO / 1024} KB. `
        + 'Usa el logotipo original, no un escaneo.',
      );
      return;
    }
    try {
      const incrustada = await comoImagenIncrustada(archivo);
      onCambio({ logo: incrustada });
      // Se mide DESPUÉS de aceptarla: el aviso informa, no bloquea. Quien mantiene esto
      // puede tener el único emblema que existe de esa autoridad.
      const medida = await medirImagen(incrustada);
      if (medida) setAvisoDeTamano(avisoDelLogo(medida.ancho, medida.alto));
    } catch {
      setProblemaDelLogo('No se pudo leer la imagen. Prueba con otro archivo.');
    }
  };

  if (!editando) return null;
  return (
    <Modal
      open
      onClose={onCerrar}
      eyebrow={`${editando.nombrePais} · ${editando.pais}`}
      title="Datos de la autoridad competente"
      icon={Building2}
      footer={(
        <>
          <Button variant="secondary" onClick={onCerrar} disabled={guardando}>Cancelar</Button>
          {/* El botón NO se deshabilita por los errores de forma, y es deliberado: un
              botón apagado no dice qué falta. Al pulsarlo se destapan los problemas y el
              usuario ve exactamente qué corregir. */}
          <Button onClick={intentarGuardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Alert tone="info" title="Esto rellena el encabezado del Formato Amarillo">
          El emblema, el nombre y los datos de contacto salen de aquí. Lo que quede vacío
          no aparece en el documento.
        </Alert>

        {/* El nombre se VE pero no se edita: lo mantiene «Autoridades y plazos», y es la
            misma columna. Dos pantallas escribiendo el mismo dato es cómo se consigue
            que dos personas se pisen sin enterarse. */}
        <Field label="Autoridad" hint="Se cambia en la pestaña «Autoridades y plazos».">
          <Input value={editando.autoridad} readOnly disabled />
        </Field>

        <Field
          label="Dirección"
          error={visible('direccion')}
          contador={cuentaAtras(editando.direccion, TOPES.direccion)}
        >
          <Input
            value={editando.direccion}
            maxLength={TOPES.direccion}
            placeholder="Av. La Molina 1915, La Molina, Lima"
            onBlur={() => tocar('direccion')}
            onChange={(e) => onCambio({ direccion: e.target.value })}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Teléfono"
            error={visible('telefono')}
            contador={cuentaAtras(editando.telefono, TOPES.telefono)}
          >
            <Input
              type="tel"
              value={editando.telefono}
              maxLength={TOPES.telefono}
              placeholder="(511) 313-3300"
              onBlur={() => tocar('telefono')}
              onChange={(e) => onCambio({ telefono: e.target.value })}
            />
          </Field>
          <Field
            label="Email"
            error={visible('correo')}
            contador={cuentaAtras(editando.correo, TOPES.correo)}
          >
            <Input
              type="email"
              value={editando.correo}
              maxLength={TOPES.correo}
              placeholder="contacto@autoridad.gob"
              onBlur={() => tocar('correo')}
              onChange={(e) => onCambio({ correo: e.target.value })}
            />
          </Field>
        </div>

        <Field
          label="Página web"
          error={visible('web')}
          contador={cuentaAtras(editando.web, TOPES.web)}
        >
          <Input
            value={editando.web}
            maxLength={TOPES.web}
            placeholder="www.senasa.gob.pe"
            onBlur={() => tocar('web')}
            onChange={(e) => onCambio({ web: e.target.value })}
          />
        </Field>

        <Field
          label="Logo de la autoridad"
          error={problemaDelLogo}
          // LA AYUDA DA LA MEDIDA, NO LA EXPLICACIÓN (operador, 25-sep-2026: «esto se me
          // hace muy confuso»). Antes contaba el porqué —la casilla, los milímetros
          // impresos, qué le pasa a un logo cuadrado— y quien rellena el formulario solo
          // necesita el número. El porqué ya está donde sirve: la vista previa ENSEÑA la
          // casilla real, y subir una imagen que no encaja dispara el aviso con su medida.
          hint={(
            <>
              Horizontal · <strong>{RECOMENDADO.ancho} × {RECOMENDADO.alto} px</strong> o
              un SVG. Uno cuadrado se verá pequeño.
            </>
          )}
        >
          <div className="space-y-2">
            {editando.logo && (
              <div className="flex items-center gap-3 rounded-lg border border-hairline bg-canvas p-2">
                {/* LA VISTA PREVIA IMITA LA CASILLA DEL DOCUMENTO, no un recuadro
                    cualquiera: con los mismos topes que `impresion.py` (max-width y
                    max-height), lo que se ve aquí es lo que saldrá impreso. Un recuadro
                    más grande enseñaría un logo que luego se encoge. */}
                {/* Las medidas van escritas en las clases y no interpoladas desde
                    `CAJA_DEL_LOGO`: Tailwind resuelve las clases al compilar y un valor
                    dinámico no generaría ninguna regla. Si la casilla del documento
                    cambia, cambian la constante Y estas cuatro clases. */}
                <span className="flex h-[58px] w-[123px] shrink-0 items-center justify-center rounded border border-dashed border-hairline bg-white">
                  <img
                    src={editando.logo}
                    alt={`Logo de ${editando.autoridad}`}
                    className="max-h-[58px] max-w-[123px] object-contain"
                  />
                </span>
                <div className="space-y-1">
                  <p className="text-meta text-ink-soft">Así entra en el Formato Amarillo.</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setProblemaDelLogo(null);
                      setAvisoDeTamano(null);
                      onCambio({ logo: '' });
                    }}
                  >
                    Quitar el logo
                  </Button>
                </div>
              </div>
            )}
            {avisoDeTamano && (
              <Alert tone="warning" title="Revisa el tamaño del logo">{avisoDeTamano}</Alert>
            )}
            <SubidaDeArchivo
              archivo={null}
              onElegir={elegirLogo}
              acepta={ACEPTA_IMAGEN}
              aria-label="Elegir el logo de la autoridad"
              disabled={guardando}
            />
          </div>
        </Field>

        {error && <Alert tone="danger" title="No se pudo guardar">{error.message}</Alert>}
      </div>
    </Modal>
  );
}

export default function AutoridadCompetente() {
  const [filas, setFilas] = useState<FilaDeAutoridad[]>([]);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [editando, setEditando] = useState<EnEdicion | null>(null);
  const [errorDelFormulario, setErrorDelFormulario] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [abriendo, setAbriendo] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      // CC-02: una fila por AUTORIDAD (un país puede tener varias, cada una con su encabezado).
      setFilas(filasPorAutoridad(await api.listarAutoridades()));
    } catch (error) {
      setFallo(aFallo(error));
    } finally {
      setCargando(false);
    }
  }, []);

  // `recargar` atrapa sus propios errores: el `.catch` solo marca la promesa como atendida.
  useEffect(() => { recargar().catch(() => {}); }, [recargar]);

  /**
   * Abrir pide el DETALLE, no reutiliza la fila.
   *
   * La lista no trae la imagen del logo a propósito (serían catorce emblemas por
   * apertura). Si el formulario se llenara con la fila, el logo llegaría vacío y guardar
   * lo BORRARÍA sin que nadie lo hubiera tocado.
   */
  const abrir = async (fila: FilaDeAutoridad) => {
    const pais = texto(fila.pais);
    const codigo = fila.codigo ? texto(fila.codigo) : null;
    setAbriendo(texto(fila.id) || pais);
    setErrorDelFormulario(null);
    try {
      const detalle = (codigo
        ? await api.verAutoridadDelPais(pais, codigo)
        : await api.verAutoridad(pais)) as RespuestaDelApi;
      setEditando({
        pais,
        codigo,
        nombrePais: texto(detalle.nombre_pais) || pais,
        autoridad: texto(detalle.autoridad ?? detalle.nombre),
        direccion: texto(detalle.autoridad_direccion),
        telefono: texto(detalle.autoridad_telefono),
        correo: texto(detalle.autoridad_correo),
        web: texto(detalle.autoridad_web),
        logo: texto(detalle.autoridad_logo),
      });
    } catch (error) {
      setFallo(aFallo(error));
    } finally {
      setAbriendo(null);
    }
  };

  const guardar = async () => {
    if (!editando) return;
    setGuardando(true);
    setErrorDelFormulario(null);
    try {
      // Se manda el cuerpo COMPLETO porque es un PUT: vaciar un teléfono mal cargado
      // tiene que ser posible, y con semántica de parche `null` significaría «no lo
      // toques». Lo vacío viaja como `null`, que es «sin dato» — el impreso lo omite.
      const contacto = {
        direccion: editando.direccion.trim() || null,
        telefono: editando.telefono.trim() || null,
        correo: editando.correo.trim() || null,
        web: editando.web.trim() || null,
        logo: editando.logo || null,
      };
      // CC-02 (CC2-23-C7): el contacto es de cada autoridad; sin código, el del país.
      if (editando.codigo) {
        await api.configurarContactoDeAutoridadDelPais(editando.pais, editando.codigo, contacto);
      } else {
        await api.configurarContactoDeAutoridad(editando.pais, contacto);
      }
      setEditando(null);
      await recargar();
    } catch (error) {
      setErrorDelFormulario(aFallo(error));
    } finally {
      setGuardando(false);
    }
  };

  const celda = (fila: FilaDeAutoridad, { key: columna }: ColumnaDeTabla): ReactNode => {
    if (columna === 'acciones') {
      return (
        <Button
          variant="secondary"
          size="sm"
          icon={Pencil}
          disabled={abriendo === (texto(fila.id) || texto(fila.pais))}
          onClick={() => void abrir(fila)}
          data-testid="autoridad-competente-editar"
        >
          {abriendo === (texto(fila.id) || texto(fila.pais)) ? 'Abriendo…' : 'Editar'}
        </Button>
      );
    }
    return CELDA_POR_COLUMNA[columna]?.(fila) ?? null;
  };

  return (
    <>
      <PageHeader
        eyebrow="Administración"
        hu="HU-23 · Formato Amarillo"
        title="Autoridad competente"
        subtitle="Los datos con los que cada autoridad se identifica en el encabezado del Formato Amarillo: emblema, dirección, teléfono, correo y web."
        icon={Building2}
      />

      {fallo && <Alert tone="danger" title="No se pudieron cargar las autoridades">{fallo.message}</Alert>}

      <Card
        title="Autoridades por país"
        subtitle="Aquí se editan las autoridades existentes."
        icon={Globe}
      >
        {cargando && <p className="text-body text-ink-soft">Cargando…</p>}
        {!cargando && filas.length > 0 && (
          <DataTable columns={COLUMNAS} rows={filas} renderCell={celda} />
        )}
        {!cargando && filas.length === 0 && !fallo && (
          <EmptyState
            icon={Building2}
            title="No hay autoridades parametrizadas"
            description="Falta la configuración inicial de los países. Avisa a soporte."
          />
        )}
      </Card>

      {/* Parrafo con iconos en linea y no `inline-flex`: como fila flexible no envolvia y a 320 px
          desbordaba la pagina (WCAG 1.4.10, auditoria AA del 2-oct-2026). */}
      <p className="mt-3 text-meta text-ink-soft">
        <MapPin size={11} className="inline align-[-1px]" /> Dirección, <Phone size={11} className="inline align-[-1px]" /> teléfono y <Mail size={11} className="inline align-[-1px]" /> correo
        se imprimen bajo el nombre de la autoridad; lo que falte se omite del documento.
      </p>

      <ModalDeContacto
        editando={editando}
        error={errorDelFormulario}
        guardando={guardando}
        onCambio={(cambio) => setEditando((p) => (p ? { ...p, ...cambio } : p))}
        onCerrar={() => { setEditando(null); setErrorDelFormulario(null); }}
        onGuardar={() => void guardar()}
      />
    </>
  );
}
